"use client";

import React from "react";

export interface GqWebOverviewBlockProps {
    tag?: string;
    title?: string;
    sublabel?: string;
    intentionTag?: string;
    intentionText?: string;
    intentionCommit?: string;
    expertiseTag?: string;
    expertiseText?: string;
    expertiseCerts?: string;
    expertiseBrand?: string;
}

export default function GqWebOverviewBlock({
    tag = "01 / OVERVIEW",
    title = "Project Narrative & Intentions",
    sublabel = "BRAND DESIGN & MARKETING",
    intentionTag = "// STRATEGIC DIRECTION",
    intentionText = "Guestiqo needed a marketing website that could translate a technical SaaS product — multi-tenant digital guidebooks — into a warm, editorial experience that property hosts would instinctively trust. We designed every section to feel like a carefully written letter, not a software sales page.",
    intentionCommit = "Commitment: Calm, editorial, and conversion-driven",
    expertiseTag = "// SCOPE & DELIVERABLES",
    expertiseText = "Kitebe designed and built the entire guestiqo.uk marketing platform from visual concept to production deployment. This included the brand identity direction, all page layouts, responsive mobile designs, SEO-optimised copy, feature discovery flows, pricing architecture, testimonial integrations, and the final Next.js production build deployed on Firebase Hosting.",
    expertiseCerts = "Stack: Next.js 15 + Tailwind CSS",
    expertiseBrand = "Firebase Hosted"
}: GqWebOverviewBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FAF8F5] border-b border-[#EDE6DD] text-[#444] font-sans antialiased w-full">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-stretch">
                    
                    {/* Section Tag */}
                    <div className="lg:col-span-3 flex flex-col justify-between">
                        <div>
                            {tag && (
                                <span className="text-xs font-mono text-[#D4A574] tracking-widest block mb-2 uppercase font-semibold">
                                    {tag}
                                </span>
                            )}
                            {title && (
                                <h2 className="text-3xl font-light text-[#1A1A1A] leading-tight">
                                    {title}
                                </h2>
                            )}
                        </div>
                        {sublabel && (
                            <div className="hidden lg:block font-mono text-[10px] text-zinc-400">
                                {sublabel}
                            </div>
                        )}
                    </div>

                    {/* Intention Card */}
                    <div className="lg:col-span-4 bg-white border border-[#EDE6DD] rounded-2xl p-8 flex flex-col justify-between space-y-6 shadow-sm">
                        <div>
                            {intentionTag && (
                                <span className="text-[10px] font-mono text-[#D4A574] uppercase tracking-widest font-semibold">
                                    {intentionTag}
                                </span>
                            )}
                            {intentionText && (
                                <p className="text-zinc-650 text-sm leading-relaxed font-light mt-4">
                                    {intentionText}
                                </p>
                            )}
                        </div>
                        <div className="h-[1px] bg-[#EDE6DD]" />
                        {intentionCommit && (
                            <div className="text-[10px] font-mono text-zinc-450">
                                {intentionCommit}
                            </div>
                        )}
                    </div>

                    {/* Capability Card */}
                    <div className="lg:col-span-5 bg-white border border-[#EDE6DD] rounded-2xl p-8 flex flex-col justify-between space-y-6 relative overflow-hidden shadow-sm">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4A574]/3 rounded-full blur-2xl pointer-events-none" />
                        
                        <div>
                            {expertiseTag && (
                                <span className="text-[10px] font-mono text-[#D4A574] uppercase tracking-widest font-semibold">
                                    {expertiseTag}
                                </span>
                            )}
                            {expertiseText && (
                                <p className="text-zinc-650 text-sm leading-relaxed font-light mt-4">
                                    {expertiseText}
                                </p>
                            )}
                        </div>
                        <div className="h-[1px] bg-[#EDE6DD]" />
                        <div className="text-[10px] font-mono text-zinc-450 flex justify-between">
                            {expertiseCerts && <span>{expertiseCerts}</span>}
                            {expertiseBrand && <span className="text-[#D4A574] font-semibold">{expertiseBrand}</span>}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}
